import React, { useState, useEffect } from 'react';
import { Save, CheckCircle2, AlertCircle, RefreshCw, Key, Store, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import { clearPointCache } from '../lib/deliveryPoints';
import ServiceGuideModal from './ServiceGuideModal';
import ServiceGuideButton from './ServiceGuideButton';

/**
 * Shared account HP publishes for dxshipmenttest.posta.hr, prefilled when the
 * merchant picks Test so onboarding does not need the README open. It maps to
 * CECODE 111111 server-side, so the Payer CECODE field stays blank.
 *
 * There is deliberately no server-side fallback to these -- hpService.js throws
 * when a credential is missing rather than quietly creating labels under a
 * shared account, so they only ever reach HP if they were saved from here.
 */
const TEST_CREDENTIALS = { username: 'testweb', password: 'testweb' };

const EMPTY_SETTINGS = {
  env: 'PROD',
  // Database-only flag (StoreConfig.allowEnvSelection); mirrors the schema
  // default. When false the merchant sees Production only and the server pins
  // env to PROD regardless of what is posted.
  allowEnvSelection: true,
  username: '',
  hasPassword: false,
  payerCECODE: '',
  defaultServiceCode: 40,
  defaultParcelSize: 'S',
  defaultWeight: 1.0,
  // Post office drop-off; mirrors StoreConfig.defaultPickupType in
  // schema.prisma and DEFAULT_PICKUP_TYPE in server/store.js.
  defaultPickupType: 2,
  // Outbound defaults: Paket24 D+2 delivered to the customer's address.
  // Mirrors StoreConfig.defaultShippingServiceCode / defaultDeliveryType in
  // schema.prisma and DEFAULT_SHIPPING_SERVICE_CODE / DEFAULT_DELIVERY_TYPE in
  // server/store.js; change all three together.
  defaultShippingServiceCode: 29,
  defaultDeliveryType: 1,
  autoEmailCustomer: true,
  merchantAddress: { companyName: '', street: '', hnum: '', city: '', zip: '', phone: '', email: '' },
};

export default function Settings({ onSaved }) {
  const [settings, setSettings] = useState(EMPTY_SETTINGS);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // The password is write-only: the server sends `hasPassword`, never the value.
  const [password, setPassword] = useState('');

  // What was in the credential fields before switching to Test, so flipping
  // back does not leave `testweb` sitting in the form ready to overwrite a real
  // production account on the next save.
  const [prodCredentials, setProdCredentials] = useState(null);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [savedSuccess, setSavedSuccess] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Which direction the HP service reference dialog opens on, or null when it
  // is closed.
  const [serviceGuide, setServiceGuide] = useState(null);

  useEffect(() => {
    api
      .getSettings()
      .then(({ settings: loaded }) => setSettings({ ...EMPTY_SETTINGS, ...loaded }))
      .catch((err) => setSaveError(`Could not load settings: ${err.message}`))
      .finally(() => setLoadingSettings(false));
  }, []);

  const patch = (changes) => setSettings((prev) => ({ ...prev, ...changes }));
  const patchAddress = (changes) =>
    setSettings((prev) => ({ ...prev, merchantAddress: { ...prev.merchantAddress, ...changes } }));

  /**
   * Switching to Test prefills HP's shared test account; switching back
   * restores whatever was there before rather than clearing the fields, so a
   * merchant who looks at Test out of curiosity does not lose the production
   * username they had typed.
   */
  const handleEnvChange = (nextEnv) => {
    if (nextEnv === settings.env) return;

    if (nextEnv === 'TEST') {
      setProdCredentials({ username: settings.username, password });
      patch({ env: 'TEST', username: TEST_CREDENTIALS.username });
      setPassword(TEST_CREDENTIALS.password);
      return;
    }

    patch({ env: 'PROD', username: prodCredentials?.username ?? '' });
    setPassword(prodCredentials?.password ?? '');
    setProdCredentials(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveError(null);
    setSavedSuccess(false);

    try {
      // Omit `password` entirely when blank so the stored one is kept.
      const payload = { ...settings };
      delete payload.hasPassword;
      delete payload.allowEnvSelection; // server-owned, never accepted from here
      if (password) payload.password = password;

      const { settings: saved } = await api.saveSettings(payload);
      setSettings({ ...EMPTY_SETTINGS, ...saved });
      setPassword('');
      // The delivery point picker holds HP's network for the life of the page,
      // and the test and production environments are different networks. A save
      // may have switched between them, so drop what was cached.
      clearPointCache();
      setSavedSuccess(true);
      setTimeout(() => setSavedSuccess(false), 4000);
      onSaved?.();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTestResult({ loading: true });
    try {
      const data = await api.pingHP();
      setTestResult({ success: true, message: JSON.stringify(data.data ?? 'reachable') });
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    }
  };

  if (loadingSettings) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <Loader2 size={32} className="spin" style={{ color: 'var(--color-brand-hp)' }} />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      <form onSubmit={handleSave}>
        <div className="polaris-card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Key size={18} color="#e20613" />
              <h2 className="card-title">Hrvatska Pošta DXWebAPI credentials</h2>
            </div>
            <button type="button" className="btn btn-secondary btn-sm" onClick={handleTestConnection}>
              <RefreshCw size={14} /> Test connection
            </button>
          </div>

          <div className="card-body">
            {testResult && (
              <div
                style={{
                  background: testResult.success ? '#e6f4ea' : '#fce8e6',
                  color: testResult.success ? '#137333' : '#c5221f',
                  padding: '0.75rem 1rem',
                  borderRadius: '6px',
                  fontSize: '0.85rem',
                  marginBottom: '1.25rem',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '0.5rem',
                }}
              >
                {testResult.loading ? (
                  <Loader2 size={16} className="spin" />
                ) : testResult.success ? (
                  <CheckCircle2 size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                ) : (
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                )}
                <span>{testResult.loading ? 'Contacting Hrvatska Pošta...' : testResult.message}</span>
              </div>
            )}

            {/*
              Who charges for what. Spelled out here because this card is where a
              merchant connects a *paid* carrier account, and the two costs are
              easy to conflate: the app subscription is charged by Shopify, the
              postage is invoiced by Hrvatska Pošta. The app is never a party to
              the second one -- see the "How you are charged" card in Billing.jsx.
            */}
            <p style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', marginBottom: '1.25rem' }}>
              These credentials belong to <strong>your own</strong> Hrvatska Pošta DXWebAPI account.
              Postage for the labels you create is invoiced by Hrvatska Pošta against that contract,
              at the rates you agreed with them. This app adds no fee or markup to postage and never
              takes payment from you &mdash; your subscription to this app is charged by Shopify and
              appears on your Shopify bill.
            </p>

            <div className="form-group">
              <label className="form-label">API environment</label>
              {settings.allowEnvSelection ? (
                <select
                  className="form-control"
                  value={settings.env}
                  onChange={(e) => handleEnvChange(e.target.value)}
                >
                  <option value="TEST">Test (dxshipmenttest.posta.hr)</option>
                  <option value="PROD">Production (dxshipment.posta.hr)</option>
                </select>
              ) : (
                // Pinned to production unless StoreConfig.allowEnvSelection is
                // turned on for this shop in the database.
                <input
                  type="text"
                  className="form-control"
                  value="Production (dxshipment.posta.hr)"
                  readOnly
                  disabled
                />
              )}
              {settings.env === 'PROD' ? (
                <p style={{ fontSize: '0.75rem', color: '#b98900', marginTop: '0.25rem' }}>
                  Labels created in production are real Hrvatska Pošta shipments. Hrvatska Pošta
                  invoices the postage to your HP contract.
                </p>
              ) : (
                <p style={{ fontSize: '0.75rem', color: 'var(--color-secondary)', marginTop: '0.25rem' }}>
                  Hrvatska Pošta&rsquo;s shared test account has been filled in below. Labels created
                  here are not real shipments and are not billed. Save to apply.
                </p>
              )}
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">API username</label>
                <input
                  type="text"
                  className="form-control"
                  autoComplete="off"
                  value={settings.username}
                  onChange={(e) => patch({ username: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">API password</label>
                <input
                  type="password"
                  className="form-control"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={settings.hasPassword ? '•••••••• (stored)' : 'Enter your HP API password'}
                  required={!settings.hasPassword}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--color-secondary)', marginTop: '0.25rem' }}>
                  {settings.hasPassword
                    ? 'Stored encrypted. Leave blank to keep the current password.'
                    : 'Stored encrypted; it is never sent back to the browser.'}
                </p>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Payer CECODE (optional)</label>
              <input
                type="text"
                className="form-control"
                maxLength={6}
                value={settings.payerCECODE}
                onChange={(e) => patch({ payerCECODE: e.target.value })}
              />
              <p style={{ fontSize: '0.75rem', color: 'var(--color-secondary)', marginTop: '0.25rem' }}>
                Leave blank unless Hrvatska Pošta should invoice postage to an HP contract other
                than the one behind your username. By default HP invoices your own contract.
              </p>
            </div>
          </div>
        </div>

        <div className="polaris-card">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Store size={18} color="#008060" />
              <h2 className="card-title">Return warehouse address</h2>
            </div>
          </div>

          <div className="card-body">
            <p style={{ fontSize: '0.83rem', color: 'var(--color-secondary)', marginBottom: '1rem' }}>
              Where customers send returns. This becomes the recipient on every HP return label.
            </p>

            <div className="form-group">
              <label className="form-label">Company / store name</label>
              <input
                type="text"
                className="form-control"
                value={settings.merchantAddress.companyName}
                onChange={(e) => patchAddress({ companyName: e.target.value })}
                required
              />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Street name</label>
                <input
                  type="text"
                  className="form-control"
                  value={settings.merchantAddress.street}
                  onChange={(e) => patchAddress({ street: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">House number</label>
                <input
                  type="text"
                  className="form-control"
                  value={settings.merchantAddress.hnum}
                  onChange={(e) => patchAddress({ hnum: e.target.value })}
                  placeholder="."
                  required
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">City</label>
                <input
                  type="text"
                  className="form-control"
                  value={settings.merchantAddress.city}
                  onChange={(e) => patchAddress({ city: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Postal code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d{5}"
                  className="form-control"
                  value={settings.merchantAddress.zip}
                  onChange={(e) => patchAddress({ zip: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Returns phone</label>
                <input
                  type="tel"
                  className="form-control"
                  value={settings.merchantAddress.phone}
                  onChange={(e) => patchAddress({ phone: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Returns email</label>
                <input
                  type="email"
                  className="form-control"
                  value={settings.merchantAddress.email}
                  onChange={(e) => patchAddress({ email: e.target.value })}
                  required
                />
              </div>
            </div>
          </div>
        </div>

        <div className="polaris-card">
          <div className="card-header">
            <h2 className="card-title">Label defaults</h2>
          </div>
          <div className="card-body">
            <p style={{ fontSize: '0.83rem', color: 'var(--color-secondary)', marginBottom: '1rem' }}>
              Pre-filled when creating a label. Staff can still change them per order.
            </p>

            <div
              style={{
                fontSize: '0.78rem',
                fontWeight: 600,
                color: 'var(--color-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                marginBottom: '0.6rem',
              }}
            >
              Shipping to customers
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Default shipping service
                  <ServiceGuideButton onClick={() => setServiceGuide('OUTBOUND')} />
                </label>
                <select
                  className="form-control"
                  value={settings.defaultShippingServiceCode}
                  onChange={(e) => patch({ defaultShippingServiceCode: Number(e.target.value) })}
                >
                  <option value={29}>Paket24 D+2 Standard</option>
                  <option value={26}>Paket24 D+1 Express</option>
                  <option value={32}>Paket24 D+3 Economy</option>
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-secondary)', marginTop: '0.25rem' }}>
                  EasyReturn services are return-only, so they are not offered here.
                </p>
              </div>

              <div className="form-group">
                <label className="form-label">Default delivery method</label>
                <select
                  className="form-control"
                  value={settings.defaultDeliveryType}
                  onChange={(e) => patch({ defaultDeliveryType: Number(e.target.value) })}
                >
                  <option value={1}>To the customer's address</option>
                  <option value={2}>To a post office</option>
                  <option value={3}>To a parcel locker</option>
                </select>
                <p style={{ fontSize: '0.75rem', color: 'var(--color-secondary)', marginTop: '0.25rem' }}>
                  Post office and parcel locker delivery need a centre code per order.
                </p>
              </div>
            </div>

            <div
              style={{
                fontSize: '0.78rem',
                fontWeight: 600,
                color: 'var(--color-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                margin: '1.25rem 0 0.6rem',
              }}
            >
              Returns from customers
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">
                  Default return service
                  <ServiceGuideButton onClick={() => setServiceGuide('RETURN')} />
                </label>
                <select
                  className="form-control"
                  value={settings.defaultServiceCode}
                  onChange={(e) => patch({ defaultServiceCode: Number(e.target.value) })}
                >
                  <option value={40}>EasyReturn D+3</option>
                  <option value={39}>EasyReturn D+3 (with outbound link)</option>
                  <option value={26}>Paket24 D+1 Express</option>
                  <option value={29}>Paket24 D+2 Standard</option>
                  <option value={32}>Paket24 D+3 Economy</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Default pickup method</label>
                <select
                  className="form-control"
                  value={settings.defaultPickupType}
                  onChange={(e) => patch({ defaultPickupType: Number(e.target.value) })}
                >
                  <option value={1}>Address pickup</option>
                  <option value={2}>Post office drop-off</option>
                  <option value={3}>Parcel locker drop-off</option>
                </select>
              </div>
            </div>

            <div
              style={{
                fontSize: '0.78rem',
                fontWeight: 600,
                color: 'var(--color-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.4px',
                margin: '1.25rem 0 0.6rem',
              }}
            >
              Both directions
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Default parcel size</label>
                <select
                  className="form-control"
                  value={settings.defaultParcelSize}
                  onChange={(e) => patch({ defaultParcelSize: e.target.value })}
                >
                  <option value="XS">XS</option>
                  <option value="S">S</option>
                  <option value="M">M</option>
                  <option value="L">L</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Default weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0.1"
                  max="30"
                  className="form-control"
                  value={settings.defaultWeight}
                  onChange={(e) => patch({ defaultWeight: Number(e.target.value) })}
                />
              </div>
            </div>
          </div>
        </div>

        {saveError && (
          <div
            style={{
              background: '#fce8e6',
              color: '#c5221f',
              padding: '0.75rem 1rem',
              borderRadius: '6px',
              fontSize: '0.85rem',
              marginTop: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <AlertCircle size={16} />
            <span>{saveError}</span>
          </div>
        )}

        <div className="settings-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1.5rem' }}>
          <div>
            {savedSuccess && (
              <span
                style={{
                  color: '#0d522c',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.3rem',
                }}
              >
                <CheckCircle2 size={16} /> Settings saved.
              </span>
            )}
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <Loader2 size={16} className="spin" /> : <Save size={16} />} Save configuration
          </button>
        </div>
      </form>

      <ServiceGuideModal
        isOpen={serviceGuide !== null}
        highlight={serviceGuide}
        onClose={() => setServiceGuide(null)}
      />
    </div>
  );
}
